import { authLogger } from "../../utils/logger";import { signUp, signIn, confirmSignUp, resendSignUpCode, type SignUpInput } from 'aws-amplify/auth';
import { parseContactPerson, formatPhoneNumber, isValidPhoneNumber, generateTenantIdFromName, generateTenantId } from '../../utils/tenantUtils';

export interface CognitoRegistrationData {
  businessName?: string; // Make businessName optional
  contactPerson: string;
  email: string;
  phone?: string; // Make phone optional too
  password: string;
}

export interface CognitoRegistrationResult {
  success: boolean;
  userId?: string;
  tenantId: string;
  firstName: string;
  lastName: string;
  message?: string;
}

// Dynamic mock mode checker (same as authService)
const getMockMode = (): boolean => {
  if (typeof window !== 'undefined') {
    if (typeof window.__TEST_MOCK_MODE__ !== 'undefined') {
      return window.__TEST_MOCK_MODE__;
    }
    
    try {
      const testMode = localStorage.getItem('TEST_MOCK_MODE');
      if (testMode !== null) {
        return testMode === 'true';
      }
    } catch (e) {
      // localStorage might not be available
    }
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const mockParam = urlParams.get('mock');
      if (mockParam !== null) {
        return mockParam === 'true';
      }
    } catch (e) {
      // URL parsing might fail
    }
  }
  
  return import.meta.env.VITE_MOCK_MODE === 'true';
};

export const cognitoRegistrationService = {
  /**
   * Register a new user in AWS Cognito with custom attributes
   */
  async registerUser(data: CognitoRegistrationData): Promise<CognitoRegistrationResult> {
    // Parse contact person into first and last name
    const { firstName, lastName } = parseContactPerson(data.contactPerson);
    
    // Generate tenant ID from first and last name
    const tenantId = generateTenantId(8);
    
    if (getMockMode()) {
      authLogger.info('🔍 CognitoRegistrationService: Using mock mode');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock validation
      if (!data.email || !data.contactPerson) {
        throw new Error('Missing required fields');
      }
      
      if (data.phone && !isValidPhoneNumber(data.phone)) {
        throw new Error('Invalid phone number format');
      }
      
      return {
        success: true,
        userId: `mock-user-${Date.now()}`,
        tenantId,
        firstName,
        lastName,
        message: 'User registered successfully (mock mode)'
      };
    }

    try {
      authLogger.info('🔍 CognitoRegistrationService: Starting user registration for:', data.email);
      
      // Validate phone number if provided
      if (data.phone && !isValidPhoneNumber(data.phone)) {
        throw new Error('Invalid phone number format. Please use international format (e.g., +41 XX XXX XX XX)');
      }
      
      // Parse contact person name
      const { firstName, lastName } = parseContactPerson(data.contactPerson);
      
      // Format phone number
      const formattedPhone = data.phone ? formatPhoneNumber(data.phone) : undefined;
      
      // Prepare user attributes
      const baseAttributes: Record<string, string> = {
        email: data.email
      };
      
      // Add optional attributes
      const optionalAttributes = [
        { key: 'given_name', value: firstName },
        { key: 'family_name', value: lastName },
        { key: 'custom:tenantId', value: tenantId }
      ];
      
      // Add phone number if provided
      if (formattedPhone) {
        optionalAttributes.push({ key: 'phone_number', value: formattedPhone });
      }
      
      // Build final user attributes
      const userAttributes = { ...baseAttributes };
      for (const attr of optionalAttributes) {
        if (attr.value) {
          userAttributes[attr.key] = attr.value;
          authLogger.info(`🔍 Adding attribute: ${attr.key} = ${attr.value}`);
        }
      }
      
      authLogger.info('🔍 CognitoRegistrationService: Final user attributes:', userAttributes);
      
      // Create sign up input
      const signUpInput: SignUpInput = {
        username: data.email,
        password: data.password,
        options: {
          userAttributes
        }
      };
      
      // Register user in Cognito
      const result = await signUp(signUpInput);
      
      authLogger.info('🔍 CognitoRegistrationService: SignUp result:', result);
      
      return {
        success: true,
        userId: result.userId,
        tenantId,
        firstName,
        lastName,
        message: 'User registered successfully'
      };
      
    } catch (error) {
      authLogger.error('❌ CognitoRegistrationService: Registration failed:', error);
      
      // Handle specific Cognito errors
      if (error instanceof Error) {
        if (error.message.includes('UsernameExistsException')) {
          throw new Error('An account with this email already exists');
        }
        if (error.message.includes('InvalidPasswordException')) {
          throw new Error('Password does not meet requirements');
        }
        if (error.message.includes('InvalidParameterException')) {
          throw new Error('Invalid registration data provided');
        }
      }
      
      throw error;
    }
  },

  /**
   * Auto-login user after successful registration
   */
  async autoLogin(email: string, password: string): Promise<void> {
    if (getMockMode()) {
      authLogger.info('🔍 CognitoRegistrationService: Auto-login in mock mode');
      return;
    }

    try {
      authLogger.info('🔍 CognitoRegistrationService: Auto-login for:', email);
      
      const result = await signIn({
        username: email,
        password: password
      });
      
      authLogger.info('🔍 CognitoRegistrationService: Auto-login result:', result);
      
      if (!result.isSignedIn) {
        throw new Error('Auto-login failed - user may need to verify email');
      }
      
    } catch (error) {
      authLogger.error('❌ CognitoRegistrationService: Auto-login failed:', error);
      throw new Error('Registration successful, but auto-login failed. Please sign in manually.');
    }
  },

  /**
   * Confirm user sign up with verification code
   */
  async confirmSignUp(email: string, confirmationCode: string): Promise<CognitoRegistrationResult> {
    if (getMockMode()) {
      authLogger.info('🔍 CognitoRegistrationService: Confirm signup in mock mode');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation
      if (confirmationCode !== '123456') {
        throw new Error('CodeMismatchException: Invalid verification code');
      }
      
      return {
        success: true,
        tenantId: 'mock-tenant',
        firstName: "Mock Firstname",
        lastName: "Mock Lastname",
        message: 'Email confirmed successfully (mock mode)'
      };
    }

    try {
      authLogger.info('🔍 CognitoRegistrationService: Confirming signup for:', email);
      
      const result = await confirmSignUp({
        username: email,
        confirmationCode: confirmationCode
      });
      
      authLogger.info('🔍 CognitoRegistrationService: Confirm signup result:', result);
      
      return {
        success: true,
        tenantId: 'confirmed', // Will be set properly in the calling function
        firstName: 'confirmed', // Will be set properly in the calling function
        lastName: 'confirmed', // Will be set properly in the calling function
        message: 'Email confirmed successfully'
      };
      
    } catch (error) {
      authLogger.error('❌ CognitoRegistrationService: Confirm signup failed:', error);
      throw error;
    }
  },

  /**
   * Resend confirmation code
   */
  async resendConfirmationCode(email: string): Promise<CognitoRegistrationResult> {
    if (getMockMode()) {
      authLogger.info('🔍 CognitoRegistrationService: Resend confirmation code in mock mode');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return {
        success: true,
        tenantId: 'mock-tenant',
        firstName: "Mock Firstname",
        lastName: "Mock Lastname",
        message: 'Confirmation code sent successfully (mock mode)'
      };
    }

    try {
      authLogger.info('🔍 CognitoRegistrationService: Resending confirmation code for:', email);
      
      const result = await resendSignUpCode({
        username: email
      });
      
      authLogger.info('🔍 CognitoRegistrationService: Resend confirmation code result:', result);
      
      return {
        success: true,
        tenantId: 'resent', // Will be set properly in the calling function
        firstName: 'resent', // Will be set properly in the calling function
        lastName: 'resent', // Will be set properly in the calling function
        message: 'Confirmation code sent successfully'
      };
      
    } catch (error) {
      authLogger.error('❌ CognitoRegistrationService: Resend confirmation code failed:', error);
      throw error;
    }
  }
};
